// WP-1.5 (§18.5 Transfer form, M9 TR-1/TR-2): from/to accounts (must differ) · amount sent
// · fee (optional) · "arrived?" toggle — off leaves it In-Transit, on settles immediately
// in the same request.
import { FEE_TYPES, parseAmountInput, type FeeType } from '@pfos/engine';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AccountPicker } from './AccountPicker';
import { Amount } from './Amount';
import { AmountField, Field, ghostButtonInlineClass, inputClass, selectClass } from './FormKit';
import { FormSheet } from './FormSheet';
import { useAccounts } from '../lib/accounts';
import { ApiError } from '../lib/api';
import { useFxRate, useSetFxRate } from '../lib/fx';
import { useRecordTransfer } from '../lib/transfers';

interface FormValues {
  fromAccountId: string;
  toAccountId: string;
  note: string;
}

const EMPTY_VALUES: FormValues = { fromAccountId: '', toAccountId: '', note: '' };

export function TransferForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const { data: accounts } = useAccounts();
  const { register, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: EMPTY_VALUES,
  });
  const [sentAmount, setSentAmount] = useState('');
  const [hasFee, setHasFee] = useState(false);
  const [feeType, setFeeType] = useState<FeeType | ''>('');
  const [feeAmount, setFeeAmount] = useState('');
  const [arrived, setArrived] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rateInput, setRateInput] = useState('');
  const recordMutation = useRecordTransfer();
  const setRateMutation = useSetFxRate();

  const fromAccountId = watch('fromAccountId');
  const toAccountId = watch('toAccountId');
  const fromAccount = (accounts ?? []).find((a) => a.id === fromAccountId);
  const toAccount = (accounts ?? []).find((a) => a.id === toAccountId);

  // FX-1/FX-3: cross-currency transfers show an indicative rate + expected receive.
  const showFx =
    fromAccount !== undefined &&
    toAccount !== undefined &&
    fromAccount.currency !== toAccount.currency;
  const { data: fxRate, isLoading: fxRateLoading } = useFxRate(
    showFx ? fromAccount.currency : undefined,
    showFx ? toAccount.currency : undefined,
  );
  const sentParsed =
    fromAccount !== undefined
      ? parseAmountInput(sentAmount, { defaultCurrency: fromAccount.currency })
      : undefined;
  const estimatedReceiveMinor =
    fxRate !== null && fxRate !== undefined && sentParsed?.ok === true
      ? Math.round(sentParsed.money.minorUnits * fxRate.rate)
      : undefined;

  // §14-13 crash-safety: one idempotency key per open compose session.
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (open) {
      reset(EMPTY_VALUES);
      setSentAmount('');
      setHasFee(false);
      setFeeType('');
      setFeeAmount('');
      setArrived(false);
      setReceivedAmount('');
      setError(null);
      setRateInput('');
      idempotencyKey.current = crypto.randomUUID();
    }
  }, [open, reset]);

  const onValid = handleSubmit(async (values) => {
    setError(null);
    if (fromAccount === undefined || toAccount === undefined) {
      setError('Choose both accounts');
      return;
    }
    if (fromAccount.id === toAccount.id) {
      setError('Choose two different accounts');
      return;
    }
    const sent = parseAmountInput(sentAmount, { defaultCurrency: fromAccount.currency });
    if (!sent.ok) return; // AmountField already shows this inline

    let feeLegs: { type: FeeType; amountMinor: number }[] | undefined;
    if (hasFee) {
      if (feeType === '') {
        setError('Choose a fee type');
        return;
      }
      const fee = parseAmountInput(feeAmount, { defaultCurrency: fromAccount.currency });
      if (!fee.ok) return; // the fee AmountField already shows its own error
      feeLegs = [{ type: feeType, amountMinor: fee.money.minorUnits }];
    }

    let receivedMinor: number | undefined;
    if (arrived) {
      const received = parseAmountInput(receivedAmount, { defaultCurrency: toAccount.currency });
      if (!received.ok) return; // the received AmountField already shows its own error
      receivedMinor = received.money.minorUnits;
    }

    try {
      await recordMutation.mutateAsync({
        id: idempotencyKey.current,
        fromAccountId: fromAccount.id,
        toAccountId: toAccount.id,
        sentMinor: sent.money.minorUnits,
        ...(feeLegs !== undefined ? { feeLegs } : {}),
        ...(values.note !== '' ? { note: values.note } : {}),
        ...(receivedMinor !== undefined ? { receivedMinor } : {}),
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the transfer');
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Transfer between accounts"
      submitLabel="Transfer"
      submitting={recordMutation.isPending}
      onSubmit={() => {
        void onValid();
      }}
    >
      <Field label="From" htmlFor="tr-from">
        <AccountPicker
          id="tr-from"
          value={fromAccountId}
          onChange={(v) => {
            reset({ ...EMPTY_VALUES, fromAccountId: v, toAccountId });
          }}
        />
      </Field>

      <Field label="To" htmlFor="tr-to">
        <AccountPicker
          id="tr-to"
          value={toAccountId}
          onChange={(v) => {
            reset({ ...EMPTY_VALUES, fromAccountId, toAccountId: v });
          }}
        />
      </Field>

      <Field label="Amount sent" htmlFor="tr-sent">
        <AmountField
          id="tr-sent"
          currency={fromAccount?.currency ?? 'PKR'}
          value={sentAmount}
          onChange={setSentAmount}
        />
      </Field>

      {showFx && !fxRateLoading && (
        <div className="mb-3 rounded-[var(--radius-control)] border border-border bg-bg p-3 text-sm">
          {fxRate ? (
            <>
              <p className="text-muted">
                Indicative rate: 1 {fromAccount.currency} ≈ {fxRate.rate} {toAccount.currency} (
                {fxRate.source}, as of {fxRate.asOf.slice(0, 10)})
              </p>
              {estimatedReceiveMinor !== undefined && (
                <p className="mt-1 text-ink">
                  Expected receive ≈{' '}
                  <Amount currency={toAccount.currency} minorUnits={estimatedReceiveMinor} />
                </p>
              )}
            </>
          ) : (
            <p className="text-muted">
              No indicative rate set for {fromAccount.currency} → {toAccount.currency} yet.
            </p>
          )}
          <div className="mt-2 flex gap-2">
            <input
              aria-label="Update indicative rate"
              className={`${inputClass} flex-1`}
              value={rateInput}
              onChange={(e) => {
                setRateInput(e.target.value);
              }}
              placeholder={fxRate ? String(fxRate.rate) : 'Rate'}
            />
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={setRateMutation.isPending}
              onClick={() => {
                const rate = Number(rateInput);
                if (!(rate > 0)) return;
                setRateMutation.mutate(
                  { from: fromAccount.currency, to: toAccount.currency, rate },
                  {
                    onSuccess: () => {
                      setRateInput('');
                    },
                  },
                );
              }}
            >
              {fxRate ? 'Update rate' : 'Set rate'}
            </button>
          </div>
        </div>
      )}

      <label className="mb-3 flex items-center gap-2 text-sm text-ink" htmlFor="tr-has-fee">
        <input
          id="tr-has-fee"
          type="checkbox"
          checked={hasFee}
          onChange={(e) => {
            setHasFee(e.target.checked);
          }}
        />
        Transfer fee (optional)
      </label>

      {hasFee && (
        <div className="mb-3 flex gap-2">
          <select
            className={`${selectClass} flex-1`}
            aria-label="Fee type"
            value={feeType}
            onChange={(e) => {
              setFeeType(e.target.value as FeeType | '');
            }}
          >
            <option value="">Fee type…</option>
            {FEE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ')}
              </option>
            ))}
          </select>
          <div className="flex-1">
            <AmountField
              id="tr-fee-amount"
              ariaLabel="Fee amount"
              currency={fromAccount?.currency ?? 'PKR'}
              value={feeAmount}
              onChange={setFeeAmount}
            />
          </div>
        </div>
      )}

      <label className="mb-3 flex items-center gap-2 text-sm text-ink" htmlFor="tr-arrived">
        <input
          id="tr-arrived"
          type="checkbox"
          checked={arrived}
          onChange={(e) => {
            setArrived(e.target.checked);
          }}
        />
        Arrived already?
      </label>

      {arrived ? (
        <Field label="Amount received" htmlFor="tr-received">
          <AmountField
            id="tr-received"
            currency={toAccount?.currency ?? 'PKR'}
            value={receivedAmount}
            onChange={setReceivedAmount}
          />
        </Field>
      ) : (
        <p className="mb-3 text-sm text-muted">
          Left as In-Transit until you mark it arrived (TR-2).
        </p>
      )}

      <Field label="Note (optional)" htmlFor="tr-note">
        <input id="tr-note" className={inputClass} {...register('note')} />
      </Field>

      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}
